import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Icon from "../components/Icon";
import GCBurnDown from "../components/GCBurnDown";
import ProcoreBudget from "../components/ProcoreBudget";
import { useData } from "../data/DataProvider";
import * as sp from "../services/sqlApi";
import { getArchetype, classifyEngine } from "../data/archetypes";
import { scoreTeam, teamVerdict } from "../services/compatibility";
import { assignmentIsCurrent } from "../services/utilization";
import { isOpenSeat } from "../services/roles";
import { generateTeamNarrative, parseNarrativeSections } from "../services/claude";
import { fetchProcoreBudget, buildSnapshotRows, groupSnapshots } from "../services/procore";
import "../styles.css";

function getInitials(name) { return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(); }
function getRoleClass(role) {
  const r = role.toLowerCase();
  if (r.includes("supt")) return "supt";
  if (r.includes("pm")) return "pm";
  if (r.includes("apm")) return "apm";
  if (r.includes("fe")) return "fe";
  return "other";
}

function scoreSubtext(score) {
  if (score >= 80) return "Well-balanced team with clear role fit. A few friction points to manage.";
  if (score >= 65) return "Generally complementary with some dynamics to watch.";
  if (score >= 50) return "Workable but expect friction in key pairings. Active management needed.";
  return "Significant trait clashes. Consider roster adjustments.";
}

function ScoreRing({ score }) {
  const deg = (score / 100) * 360;
  const color = score >= 80 ? "#3db87a" : score >= 65 ? "#dfab01" : score >= 50 ? "#e8a04a" : "#e08a8a";
  const textColor = score >= 80 ? "var(--green)" : score >= 65 ? "var(--gold-text)" : "var(--red)";
  return (
    <div className="score-ring" style={{ background: `conic-gradient(${color} 0deg ${deg}deg, var(--bg-elevated) ${deg}deg 360deg)` }}>
      <div className="score-ring-inner">
        <div className="score-ring-num" style={{ color: textColor }}>{score}</div>
        <div className="score-ring-max">/ 100</div>
      </div>
    </div>
  );
}

function NarrativeBlocks({ narrative }) {
  const s = parseNarrativeSections(narrative);
  const renderText = (txt) => txt.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>);
  return (
    <>
      {s.triumphs && (
        <div className="narr-block triumphs">
          <div className="narr-title"><Icon name="trophy" /> Team Triumphs</div>
          {renderText(s.triumphs)}
        </div>
      )}
      {s.struggles && (
        <div className="narr-block struggles">
          <div className="narr-title"><Icon name="alert" /> Team Struggles</div>
          {renderText(s.struggles)}
        </div>
      )}
      {s.guidance && (
        <div className="narr-block guidance">
          <div className="narr-title"><Icon name="compass" /> Leadership Guidance</div>
          {renderText(s.guidance)}
        </div>
      )}
    </>
  );
}

// Shows PX / Project Accountant as supporting personnel (display only for now).
function ProjectSupport({ project, people }) {
  const support = [
    { role: "Project Executive", name: project.ProjectExecutive },
    { role: "Project Accountant", name: project.ProjectAccountant },
  ].filter(s => s.name);
  if (support.length === 0) return null;
  return (
    <div className="card" style={{ marginTop: "18px" }}>
      <div className="card-header"><div className="card-title">Project Support</div></div>
      {support.map((s, i) => {
        const person = people.find(p => p.FullName === s.name);
        const arch = person ? getArchetype(person.Archetype) : null;
        return (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 1fr auto", alignItems: "center", gap: "14px", padding: "12px 0", borderBottom: i < support.length - 1 ? "1px solid var(--border)" : "none" }}>
            <div className="avatar other" style={{ width: "38px", height: "38px", fontSize: "12px" }}>{getInitials(s.name)}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "14px" }}>{person ? <Link to={`/people/${person.Id}`} style={{ color: "inherit", textDecoration: "none" }}>{s.name}</Link> : s.name}</div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1px" }}>{s.role}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              {person && arch ? (
                <span className="person-archetype" style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: "5px" }}>
                  <Icon name={arch.icon} size={13} /> {person.Archetype}
                </span>
              ) : (
                <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>No profile</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ProjectDetail() {
  const { jobNumber } = useParams();
  const decodedJob = decodeURIComponent(jobNumber);
  const { projects, teamMembers: allTeamMembers, people, billableRates, loading, error, getToken,
    getBudgetSnapshots, saveBudgetSnapshotRows, isAdmin, myEmail } = useData();
  const [narrative, setNarrative] = useState(null);
  const [score, setScore] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [showSnap, setShowSnap] = useState(false);

  // ── Procore budget snapshots (shared by the Labor Budget card + GC burn-down) ──
  const [budgetSnaps, setBudgetSnaps] = useState([]);
  const [budgetLoading, setBudgetLoading] = useState(true);
  const [budgetPulling, setBudgetPulling] = useState(false);
  const [budgetErr, setBudgetErr] = useState(null);

  const loadBudget = useCallback(async () => {
    setBudgetLoading(true);
    try { setBudgetSnaps(groupSnapshots(await getBudgetSnapshots(decodedJob))); }
    catch (e) { setBudgetErr(e.message); }
    finally { setBudgetLoading(false); }
  }, [getBudgetSnapshots, decodedJob]);

  useEffect(() => { loadBudget(); }, [loadBudget]);

  // Load any saved narrative for this project from SharePoint.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const existing = await sp.getNarrative(token, decodedJob);
        if (!cancelled && existing) {
          setNarrative(existing.NarrativeText || "");
          setScore(existing.CompatibilityScore ?? null);
        }
      } catch (e) { /* no saved narrative yet — fine */ }
    })();
    return () => { cancelled = true; };
  }, [decodedJob, getToken]);

  if (loading) {
    return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading project…</div></div></div>;
  }
  if (error) {
    return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;
  }

  const project = projects.find(p => p.Title === decodedJob);
  const jobMembers = allTeamMembers.filter(m => m.ProjectJobNumber === decodedJob);
  // Roster shows only people still on the team — anyone whose end date has
  // passed has rolled off and is hidden here (GC burn still uses the full set).
  const teamMembers = jobMembers.filter(m => assignmentIsCurrent(m));

  if (!project) {
    return (
      <div className="page">
        <Link to="/" className="back-btn"><Icon name="arrow-left" /> Back to Projects</Link>
        <div className="empty"><div className="empty-text">Project not found.</div></div>
      </div>
    );
  }

  const profiledMembers = teamMembers.filter(m => people.find(p => p.FullName === m.PersonName));
  const canGenerate = profiledMembers.length >= 2;
  const profiledPeople = profiledMembers.map(m => people.find(p => p.FullName === m.PersonName));
  const teamScore = scoreTeam(profiledPeople);

  const engine = { gas: 0, brake: 0, glue: 0 };
  profiledMembers.forEach(m => {
    const person = people.find(p => p.FullName === m.PersonName);
    if (person) engine[classifyEngine(person)]++;
  });

  const handleGenerate = async () => {
    setGenerating(true); setGenError(null);
    try {
      const result = await generateTeamNarrative(teamMembers, people, await getToken());
      setNarrative(result.narrative);
      setScore(result.score);
      // Persist to SharePoint so it survives refreshes (best-effort).
      try {
        const token = await getToken();
        await sp.saveNarrative(token, {
          Title: decodedJob,
          ProjectJobNumber: decodedJob,
          NarrativeText: result.narrative,
          CompatibilityScore: result.score ?? null,
          GeneratedDate: new Date().toISOString(),
        });
      } catch (saveErr) { console.warn("Narrative save failed:", saveErr); }
    } catch (err) {
      setGenError("Failed to generate analysis. Check your API connection.");
      console.error(err);
    } finally { setGenerating(false); }
  };

  const verdict = teamScore ? { label: teamVerdict(teamScore.score), sub: scoreSubtext(teamScore.score) } : null;
  const dueDate = project.CompletionDate ? new Date(project.CompletionDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;

  // Pull the current labor budget from Procore and save a dated snapshot.
  const handlePullBudget = async () => {
    setBudgetPulling(true); setBudgetErr(null);
    try {
      const data = await fetchProcoreBudget(project.ProcoreProjectId, await getToken());
      if (!data.lines || data.lines.length === 0) throw new Error("Procore returned no Labor lines for this project's Hardaway View REV 1.");
      const rows = buildSnapshotRows({ jobNumber: project.Title, lines: data.lines, pulledBy: myEmail });
      await saveBudgetSnapshotRows(rows);
      await loadBudget();
    } catch (e) { setBudgetErr(e.message); }
    finally { setBudgetPulling(false); }
  };

  // GC burn-down prefers Procore numbers when a snapshot exists; otherwise it
  // falls back to the manually-entered Starting GC / GC Remaining on the project.
  const latestBudget = budgetSnaps.length ? budgetSnaps[budgetSnaps.length - 1].totals : null;

  return (
    <div className="page">
      <Link to="/" className="back-btn"><Icon name="arrow-left" /> Back to Projects</Link>

      {/* Header */}
      <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="font-mono text-accent text-sm" style={{ marginBottom: "4px", fontWeight: 700 }}>{project.Title}</div>
          <div className="page-title" style={{ fontSize: "26px" }}>{project.JobName}</div>
          <div className="page-subtitle">{project.Address}{project.Address && dueDate ? " · " : ""}{dueDate ? `Due ${dueDate}` : ""}</div>
        </div>
        <div className="flex gap-8" style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          {project.ProjectExecutive && <span className="meta-tag px">PX: {project.ProjectExecutive}</span>}
          {project.ProjectAccountant && <span className="meta-tag acct">{project.ProjectAccountant}</span>}
          {project.Status && <span className={`status-pill ${project.Status.toLowerCase().replace(/\s+/g, "-")}`}>{project.Status}</span>}
        </div>
      </div>

      {/* Score bar */}
      {teamScore && verdict && (
        <div className="scorebar">
          <ScoreRing score={teamScore.score} />
          <div className="scorebar-text">
            <div className="scorebar-label">{verdict.label}</div>
            <div className="scorebar-sub">{verdict.sub}</div>
          </div>
          <div className="engine-balance">
            <div className="engine-item"><div className="engine-num" style={{ color: "var(--gold)" }}>{engine.gas}</div><div className="engine-label">Gas</div></div>
            <div className="engine-item"><div className="engine-num" style={{ color: "var(--blue)" }}>{engine.brake}</div><div className="engine-label">Brake</div></div>
            <div className="engine-item"><div className="engine-num" style={{ color: "var(--green-bar)" }}>{engine.glue}</div><div className="engine-label">Glue</div></div>
          </div>
        </div>
      )}

      <div className="grid-2-resp" style={{ alignItems: "start" }}>
        {/* Roster */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Team Roster</div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{teamMembers.filter(m => !isOpenSeat(m)).length} staffed · {teamMembers.filter(isOpenSeat).length} open</div>
          </div>
          {teamMembers.length === 0 ? (
            <div className="empty"><div className="empty-text">No one assigned yet. Add the team in Admin Console → Manage Teams.</div></div>
          ) : teamMembers.map((member, i) => {
            const open = isOpenSeat(member);
            const person = open ? null : people.find(p => p.FullName === member.PersonName);
            const arch = person ? getArchetype(person.Archetype) : null;
            return (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 1fr auto", alignItems: "center", gap: "14px", padding: "13px 0", borderBottom: i < teamMembers.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div className={`avatar ${getRoleClass(member.ProjectRole)}`} style={{ width: "38px", height: "38px", fontSize: "12px", opacity: open ? 0.5 : 1 }}>{open ? "—" : getInitials(member.PersonName)}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "14px", fontStyle: open ? "italic" : "normal", color: open ? "var(--gold-text)" : undefined }}>{open ? "Open seat" : member.PersonName}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "1px" }}>{member.ProjectRole}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  {open ? (
                    <span style={{ fontSize: 10, fontWeight: 800, color: "var(--gold-text)", background: "var(--gold-soft)", border: "1px dashed var(--gold)", borderRadius: 6, padding: "3px 8px" }}>NEEDS FILL</span>
                  ) : person ? (
                    <span className="person-archetype" style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      <Icon name={arch.icon} size={13} /> {person.Archetype}
                    </span>
                  ) : (
                    <Link to="/admin/import" style={{ fontSize: "12px", color: "var(--gold-text)", textDecoration: "none", fontWeight: 600 }}>+ Add profile</Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Trait snapshot */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Trait Snapshot <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>· {profiledMembers.length} profiled</span></div>
            <button className="btn btn-ghost" style={{ padding: "3px 12px", fontSize: 12 }} onClick={() => setShowSnap(s => !s)}>{showSnap ? "Hide" : "Show"}</button>
          </div>
          {showSnap && (profiledMembers.length === 0 ? (
            <div className="empty"><div className="empty-icon"><Icon name="chart" /></div><div className="empty-text">Load Culture Index profiles to see trait analysis.</div></div>
          ) : (
            <>
              <div className="trait-legend" style={{ marginBottom: 10 }}>
                <span className="tl-item"><span className="tl-dot survey" />Survey</span>
                <span className="tl-item"><span className="tl-dot job" />On Job</span>
              </div>
              {profiledMembers.map((member, i) => {
              const person = people.find(p => p.FullName === member.PersonName);
              return (
                <div key={i} className="snap-person">
                  <div className="snap-person-head">
                    <span className="snap-person-name">{member.PersonName}</span>
                    <span className="snap-person-role">{member.ProjectRole}</span>
                  </div>
                  {["A", "B", "C", "D"].map(t => {
                    const sv = person[`Survey_${t}`];
                    const jv = person[`Job_${t}`] ?? sv;
                    const drain = Math.abs(jv - sv) >= 3;
                    return (
                      <div key={t} className="snap-trait">
                        <div className="snap-trait-letter">{t}</div>
                        <div className="snap-trait-bars">
                          <div className="snap-bar-track"><div className="snap-bar-mid" /><div className="snap-bar-fill survey" style={{ width: `${(sv / 10) * 100}%` }} /></div>
                          <div className="snap-bar-track"><div className="snap-bar-mid" /><div className="snap-bar-fill job" style={{ width: `${(jv / 10) * 100}%` }} /></div>
                        </div>
                        <div className="snap-trait-vals">
                          <span className="snap-val survey">{sv}</span>
                          <span className="snap-val job">{jv}</span>
                        </div>
                        {drain && <span className="snap-drain" title="Adapting on the job — possible energy drain">⚠</span>}
                      </div>
                    );
                  })}
                </div>
              );
              })}
            </>
          ))}
        </div>
      </div>

      {/* Project Support (PX / Accountant) */}
      <ProjectSupport project={project} people={people} />

      {/* GC Burn-Down (uses Procore actuals when available, else manual entry) */}
      <GCBurnDown project={project} members={jobMembers.filter(m => !isOpenSeat(m))} rates={billableRates} procore={latestBudget} />

      {/* Procore labor budget (live pull + history) */}
      <ProcoreBudget snaps={budgetSnaps} loading={budgetLoading} pulling={budgetPulling}
        err={budgetErr} onPull={handlePullBudget} procoreId={project.ProcoreProjectId} isAdmin={isAdmin} />

      {/* Analysis */}
      <div className="card" style={{ marginTop: "18px" }}>
        <div className="card-header">
          <div className="card-title">Team Analysis</div>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || !canGenerate}>
            {generating ? <span className="flex items-center gap-8"><span className="spinner" style={{ width: "14px", height: "14px" }} /> Analyzing...</span>
              : narrative ? "Regenerate Analysis" : "Generate Team Analysis"}
          </button>
        </div>
        {!canGenerate && (
          <div style={{ color: "var(--text-muted)", fontSize: "13px" }}>
            At least 2 team members need Culture Index profiles to generate an analysis.{" "}
            <Link to="/admin/import" style={{ color: "var(--gold-text)" }}>Add profiles →</Link>
          </div>
        )}
        {genError && <div style={{ color: "var(--red)", fontSize: "13px" }}>{genError}</div>}
        {narrative && !generating && <NarrativeBlocks narrative={narrative} />}
        {!narrative && !generating && canGenerate && (
          <div className="empty"><div className="empty-icon"><Icon name="brain" /></div>
            <div className="empty-text">Generate an AI compatibility report with triumphs, struggles, and leadership guidance.</div></div>
        )}
      </div>
    </div>
  );
}
